import { useContext, useEffect, useRef, useState, useMemo } from "react";
import { useMapEvents } from "react-leaflet";
import { LatLngBounds, LatLngExpression } from "leaflet";

import { LeafletContext } from "../../../App";
import { markersSelector, mapMarkersSelector, resizeMarker, resizeDefaultMarkers, spaceNumbersSelector, spaceLettersSelector, SpaceType, useAppDispatch, useAppSelector } from "../../../store";

import { HTMLMapOverlay } from "../../HTMLMapOverlay";
import { useRect } from "../../../hooks/useRect";
import { Icon } from "../Icon";

const RESIZE_HANDLE_SIZE = 16;

export function ResizableSpace({ space, position }: { space: SpaceType, position: LatLngExpression }) {
  const dispatch = useAppDispatch();
  const markers = useAppSelector(markersSelector);
  const defaultMarkers = useAppSelector(mapMarkersSelector);
  const number = useAppSelector(spaceNumbersSelector)[space.id];
  const letter = useAppSelector(spaceLettersSelector)[space.id];
  const currentMarker = useMemo(() => {
    return markers.find((m) => m.id === space.markerId) ?? defaultMarkers;
  }, [markers, defaultMarkers, space.markerId]);

  const svgRef = useRef<SVGSVGElement | null>(null);

  const [isDragging, setIsDragging] = useState(false);
  const [size, setSize] = useState(currentMarker.size.split('x').map(Number) as [number, number]);
  const [scale, setScale] = useState(1);

  const [mapBounds, setMapBounds] = useState<LatLngBounds | null>(null);
  const map = useMapEvents({
    move() {
      setMapBounds(map.getBounds());
    },
    zoom() {
      setMapBounds(map.getBounds());
    },
  });

  const { rect, measureRef } = useRect<HTMLDivElement>(undefined, [mapBounds]);

  // Ensure marker becomes unhovered so it shows up with the correct scale
  const { setHoveredMarker } = useContext(LeafletContext);
  useEffect(() => {
    setHoveredMarker('');
  }, [setHoveredMarker]);

  return (
    <HTMLMapOverlay
      latlng={position}
      anchor="center"
    >
      <div
        style={{
          position: 'absolute',
          inset: isDragging ? '-100vw' : '0',
          userSelect: 'none',
          translate: `0 -${size[1] / 2}px`,
        }}
        onMouseMove={(e) => {
          if (!isDragging || !rect) return;

          const offset = {
            x: e.pageX - rect.left - RESIZE_HANDLE_SIZE / 2,
            y: e.pageY - rect.top - RESIZE_HANDLE_SIZE / 2,
          };

          svgRef.current?.style.setProperty('translate', `${offset.x}px ${offset.y}px`);

          const scale = Math.hypot(
            e.pageX - (rect.right - rect.width / 2) - RESIZE_HANDLE_SIZE / 2,
            e.pageY - rect.bottom - RESIZE_HANDLE_SIZE / 2,
          ) / Math.hypot(size[0] / 2, size[1]);

          setScale(scale);
        }}
        onMouseUp={() => {
          if (!isDragging) return;

          svgRef.current?.style.removeProperty('translate');
          setIsDragging(false);
          const [w, h] = size.map((s) => Math.round(s * scale));
          if (currentMarker.id === 'default') {
            dispatch(resizeDefaultMarkers({ size: `${w}x${h}` }));
          } else {
            dispatch(resizeMarker({
              ...currentMarker,
              size: `${w}x${h}`,
            }));
          }
          setSize([w, h]);
          setScale(1);
        }}
      >
        <div
          ref={measureRef}
          style={{
            colorScheme: 'only light',
            position: 'relative',
            width: size[0] + 'px',
            height: size[1] + 'px',
            inset: '50%',
            translate: '-50% -50%',
          }}
        >
          <Icon
            marker={currentMarker}
            number={number}
            letter={letter}
            style={{
              position: 'absolute',
              width: size[0] + 'px',
              height: size[1] + 'px',
              transform: `scale(${scale})`,
              transformOrigin: 'center bottom',
            }}
          />
          <svg
            ref={svgRef}
            viewBox="-1 -1 2 2"
            style={{
              position: 'absolute',
              width: `${RESIZE_HANDLE_SIZE}px`, height: `${RESIZE_HANDLE_SIZE}px`,
              inset: '50%',
              cursor: 'nwse-resize',
              transformOrigin: `calc(-${size[0] / 2}px + 50%) calc(-${size[1] / 2}px + 50%)`,
              transform: `translate(-${size[0] / 2}px, -${size[1] / 2}px)`,
            }}
            onMouseDown={() => {
              setIsDragging(true);
            }}
          >
            <circle cx={0} cy={0} r={1 - 0.25} fill="white" stroke="var(--brand)" strokeWidth="0.25" />
          </svg>
        </div>
      </div>
    </HTMLMapOverlay>
  );
};
